/**
 * useBatchOnboarding — data hooks for the OptiHub Batch Onboarding console
 * (Team / Folder Structure Batch Creator).
 *
 * These call staff-api, which forwards to the OptiSigns public GraphQL gateway
 * server-side with the customer's API key as a Bearer token (the key never
 * touches the browser bundle or the audit log — see the staff-api resolver /
 * optisigns-public-api.client). CSV parsing stays on the client; the parsed
 * rows + key travel in the GraphQL variables.
 */
import { useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  STAFF_BATCH_CREATE_FOLDERS,
  STAFF_BATCH_CREATE_TEAMS,
  STAFF_BATCH_LIST_TEAMS,
} from '@staff-app/graphql-client';
import type { FolderRow, TeamRow } from '../batch/csv';

/** Per-row outcome for a batch create. */
export interface BatchRowResult {
  rowNumber: number;
  ok: boolean;
  name: string;
  /** Created entity id when ok (or the existing team id on a duplicate). */
  id?: string | null;
  /** Confirmation text, or the failure reason. */
  message: string;
}

/** A team as returned by the list-teams query. */
export interface TeamListItem {
  id: string;
  name: string;
  description?: string | null;
  usedScreens?: number | null;
  maxScreens?: number | null;
}

export function useBatchOnboarding() {
  const [createTeamsMut] = useMutation<
    { staffBatchCreateTeams: BatchRowResult[] },
    { apiKey: string; rows: { name: string; description: string | null; maxQuantity: string | null }[] }
  >(STAFF_BATCH_CREATE_TEAMS);

  const [createFoldersMut] = useMutation<
    { staffBatchCreateFolders: BatchRowResult[] },
    {
      apiKey: string;
      teamId: string;
      rows: { name: string; type: string; description: string | null; securityTeams: string | null }[];
    }
  >(STAFF_BATCH_CREATE_FOLDERS);

  const [listTeamsQuery] = useLazyQuery<
    { staffBatchListTeams: TeamListItem[] },
    { apiKey: string }
  >(STAFF_BATCH_LIST_TEAMS, { fetchPolicy: 'network-only' });

  const createTeams = useCallback(
    async (apiKey: string, rows: TeamRow[]): Promise<BatchRowResult[]> => {
      const res = await createTeamsMut({
        variables: {
          apiKey,
          rows: rows.map((r) => ({
            name: r.name,
            description: r.description || null,
            maxQuantity: r.maxQuantity || null,
          })),
        },
      });
      return res.data?.staffBatchCreateTeams ?? [];
    },
    [createTeamsMut],
  );

  const fetchTeams = useCallback(
    async (apiKey: string): Promise<TeamListItem[]> => {
      const res = await listTeamsQuery({ variables: { apiKey } });
      if (res.error) throw res.error;
      return res.data?.staffBatchListTeams ?? [];
    },
    [listTeamsQuery],
  );

  const createFolders = useCallback(
    async (apiKey: string, teamId: string, rows: FolderRow[]): Promise<BatchRowResult[]> => {
      const res = await createFoldersMut({
        variables: {
          apiKey,
          teamId,
          rows: rows.map((r) => ({
            name: r.name,
            type: r.type,
            description: r.description || null,
            securityTeams: r.securityTeams || null,
          })),
        },
      });
      return res.data?.staffBatchCreateFolders ?? [];
    },
    [createFoldersMut],
  );

  return { createTeams, fetchTeams, createFolders };
}
